// Single on/off switch for the whole GPS flow: permission prompting, the
// /location-required gate, and "current location" in address-select. While
// false, nothing touches geolocation and location comes only from a manually
// picked address — flip back to false to kill the flow again if needed.
export const GPS_FLOW_ENABLED = true;

// One wall-clock budget for the whole resolve: the permission-dialog wait plus
// fix acquisition. Browsers vary on whether the prompt wait counts against the
// spec timeout (Chrome excludes it), so the watchdog is what actually enforces
// the cap. Generous on purpose — settling while the dialog is still open would
// navigate away behind it and discard a late grant's fix.
const GEO_BUDGET_MS = 60_000;
const GEO_OPTIONS = {
  // Low accuracy is right for "clubs near me" and is far faster and cheaper on
  // battery than a GPS fix.
  enableHighAccuracy: false,
  timeout: GEO_BUDGET_MS,
  maximumAge: 5 * 60 * 1000,
};

/**
 * The browser's geolocation permission state, read *without* prompting.
 *
 * Never throws. Returns "unknown" where navigator.permissions is missing (Safari < 16),
 * which callers must treat as "can't tell" rather than as any particular state.
 *
 * Inside an embedded Android WebView (the partner apps) even "prompt" is a
 * "can't tell": the host app owns the real OS permission and Chromium never
 * records its answers, so a denied user still reads "prompt" here. Only
 * "granted" and "denied" can be trusted there — see hasKnownGpsDenial below.
 *
 * @returns {Promise<"granted"|"denied"|"prompt"|"unknown">}
 */
export async function readGeolocationPermission() {
  try {
    const status = await navigator.permissions?.query({ name: "geolocation" });
    return status?.state ?? "unknown";
  } catch {
    return "unknown";
  }
}

/**
 * Live subscription to the geolocation permission state, without prompting.
 *
 * Calls `onChange` with the current state right away and again on every change —
 * so a screen showing permission-dependent UI self-corrects when the user answers
 * a prompt or flips the setting while the screen is open. Where the permissions
 * API is missing (Safari < 16, some WebViews) `onChange` is never called: the
 * caller's default must be the "can't tell" rendering, same as "unknown" above.
 *
 * @param {(state: "granted"|"denied"|"prompt") => void} onChange
 * @returns {() => void} unsubscribe
 */
export function watchGeolocationPermission(onChange) {
  let disposed = false;
  let watchedStatus = null;

  (async () => {
    try {
      const status = await navigator.permissions?.query({ name: "geolocation" });
      if (!status || disposed) return;
      watchedStatus = status;
      status.addEventListener("change", handleChange);
      onChange(status.state);
    } catch {
      // No permissions API — stay silent, caller keeps its default rendering.
    }
  })();

  function handleChange() {
    if (!disposed) onChange(watchedStatus.state);
  }

  return () => {
    disposed = true;
    watchedStatus?.removeEventListener("change", handleChange);
  };
}

/**
 * Whether the app has *witnessed* a permission denial that, as far as it can
 * tell, still stands. Purely evidence-based, never a guess:
 *
 * - A live "granted" reading wins over everything — it clears the stored
 *   evidence and answers false, so a permission re-enabled in the device
 *   settings self-heals on the very next check.
 * - A live "denied" reading is a denial regardless of stored state.
 * - Only the unreadable states ("prompt"/"unknown") fall back to the stored
 *   flag. Inside the partner apps' WebView the host owns the real OS permission
 *   and this API keeps reading "prompt" even for a denied user — there, the
 *   only truth available is what a previous real attempt came back with. The
 *   flag is written exclusively by requestLocation below (set on a code-1
 *   failure, cleared on any successful fix), so in the WebView it also clears
 *   the first time a tap-initiated attempt succeeds after the user re-enables
 *   location.
 *
 * Home's entry refresh keys on this: attempt (and let the dialog appear) unless
 * a denial is known — because in the WebView a request made while OS-denied
 * makes the Clalit app open the device settings, which must never happen twice
 * and never without the flow having been answered by the user once.
 */
export async function hasKnownGpsDenial() {
  const state = await readGeolocationPermission();
  if (state === "granted") {
    localStorage.removeItem("gpsDenied");
    return false;
  }
  if (state === "denied") return true;
  return localStorage.getItem("gpsDenied") === "1";
}

/**
 * Whether a GPS fix has ever succeeded on this device (flag written in the core
 * below on every successful fix). Lets callers treat an "unknown" permission
 * state — WebViews / older Safari without navigator.permissions — as effectively
 * granted: a past fix means permission was approved at some point, so a silent
 * getCurrentPosition won't raise a first-time prompt.
 */
export function hasObtainedGpsFix() {
  return localStorage.getItem("gpsFixObtained") === "1";
}

/**
 * Asks the browser for the user's coordinates, once, unconditionally.
 *
 * This may raise the permission dialog, and inside the partner apps' WebView a
 * request made while the OS permission is denied makes the host app open the
 * device settings. So this belongs ONLY to explicit user actions, where either
 * is the expected response to the tap: the welcome CTA, the location screen's
 * CTA, and the address screen's "current location" row. Everything that resolves
 * in the background — home's entry refresh, the address screen's mount-time
 * subtitle, LayoutV2's deep-link resolve — must go through
 * resolveLocationIfGranted (or its own stricter gate) instead.
 *
 * Never throws and never hangs: every path settles, including denial, an
 * unanswered prompt (the watchdog, after the full budget) and plain http, where
 * geolocation is silently unavailable.
 *
 * @returns {Promise<{coords: {latitude: number, longitude: number}} | {error: "denied"|"unavailable"|"timeout"}>}
 */
export function requestLocation() {
  if (!GPS_FLOW_ENABLED) {
    return Promise.resolve({ error: "unavailable" });
  }
  if (!navigator.geolocation || !window.isSecureContext) {
    return Promise.resolve({ error: "unavailable" });
  }

  return new Promise((resolve) => {
    let settled = false;

    const settle = (result) => {
      if (settled) return;
      settled = true;
      clearTimeout(watchdog);
      resolve(result);
    };

    const watchdog = setTimeout(() => settle({ error: "timeout" }), GEO_BUDGET_MS);

    navigator.geolocation.getCurrentPosition(
      (position) => {
        // Outcome bookkeeping (the only writer of both flags): a fix proves the
        // permission is granted right now, so any stored denial is stale.
        localStorage.setItem("gpsFixObtained", "1");
        localStorage.removeItem("gpsDenied");
        settle({ coords: { latitude: position.coords.latitude, longitude: position.coords.longitude } });
      },
      (error) => {
        // Code 1 is a witnessed permission denial — remember it for the states
        // where the permission API can't answer (see hasKnownGpsDenial).
        if (error?.code === 1) localStorage.setItem("gpsDenied", "1");
        settle({ error: error?.code === 1 ? "denied" : "unavailable" });
      },
      GEO_OPTIONS,
    );
  });
}

/**
 * Background-safe resolve: attempts a fix only when it provably cannot raise a
 * dialog or a settings jump — permission already "granted", or "unknown" (no
 * permissions API) with a past successful fix on record and no denial witnessed
 * since (a witnessed denial invalidates the past-fix inference). Anything else
 * returns { error: "skipped" } without touching getCurrentPosition at all. The
 * permission is re-read on every call, so a grant flipped in the device
 * settings takes effect on the next call by itself.
 */
export async function resolveLocationIfGranted() {
  if (!GPS_FLOW_ENABLED) return { error: "skipped" };
  const permission = await readGeolocationPermission();
  const unknownButPreviouslyGranted =
    permission === "unknown" && hasObtainedGpsFix() && localStorage.getItem("gpsDenied") !== "1";
  if (permission !== "granted" && !unknownButPreviouslyGranted) {
    return { error: "skipped" };
  }
  return requestLocation();
}
